var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { createRef, memo, useMemo, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { useKeyboardFocusState } from '../../utils/useKeyboardFocusState';
import { FadeReveal } from '../FadeReveal/index';
import { sizes } from '../Select/constants';
import { addonAlignments, borderTypes, textInputSizes, textInputTypes, } from './constants';
import { borderWidth, counterStyles, errorMessageStyles, errorStyles, inputHeights, inputStyles as baseInputStyles, placeholderContainerStyles, useErrorSpacingStyles, useLabelStyles, usePlaceholderContentStyles, useInputContainerStyles, } from './styles';
import { TextInputAddon, } from './TextInputAddon';
const verticalPadding = 6;
const inputValueVerticalSpacingStyles = {
    [sizes.medium]: css `
    padding-bottom: ${styleUtils.rem(verticalPadding)};
    padding-top: calc(${theme.spacing.x4} + (${theme.spacing.base} / 2));
  `,
    [sizes.small]: css `
    padding-bottom: calc(${theme.spacing.base} / 2);
    padding-top: ${theme.spacing.x4};
  `,
};
const bottomBorderStyles = css `
  border-top: 0;
  border-right: 0;
  border-left: 0;
  border-bottom-color: ${theme.colors.gray700};
  border-bottom-style: solid;
  border-bottom-width: ${styleUtils.rem(borderWidth)};
`;
const helperTextStyles = css `
  ${theme.typeStyles.bodySmallStyles}
  color: ${theme.colors.gray500};
  margin-left: ${theme.spacing.base};

  input:focus ~ label & {
    ${styleUtils.transition([
    'opacity',
])}
    opacity: 0;
  }
`;
const defaultProps = {
    addons: [],
    borderType: borderTypes.full,
    disabled: false,
    error: '',
    hasErrorSpacing: false,
    hint: null,
    id: '',
    inputMode: '',
    isCounterVisible: false,
    label: '',
    mask: false,
    name: null,
    onBlur: () => { },
    onChange: () => { },
    pattern: null,
    placeholder: null,
    required: false,
    size: textInputSizes.medium,
    type: textInputTypes.text,
};
const TextInput = (_a) => {
    var { addons = defaultProps.addons, ariaDescribedBy, ariaLabel, ariaLabelledBy, autoCapitalize, autoComplete, autoCorrect, borderType = defaultProps.borderType, disabled, error, hasErrorSpacing, hint, id, inputMode, inputRef = createRef(), isCounterVisible, label, mask, maxLength, name, onBlur = defaultProps.onBlur, onChange = defaultProps.onChange, onFocus, onKeyDown, onKeyUp, pattern, placeholder, required, size = defaultProps.size, touched, type = defaultProps.type, value } = _a, rest = __rest(_a, ["addons", "ariaDescribedBy", "ariaLabel", "ariaLabelledBy", "autoCapitalize", "autoComplete", "autoCorrect", "borderType", "disabled", "error", "hasErrorSpacing", "hint", "id", "inputMode", "inputRef", "isCounterVisible", "label", "mask", "maxLength", "name", "onBlur", "onChange", "onFocus", "onKeyDown", "onKeyUp", "pattern", "placeholder", "required", "size", "touched", "type", "value"]);
    const isKeyboardFocused = useKeyboardFocusState(inputRef);
    const leftAddons = addons.filter((addon) => addon.align === addonAlignments.left);
    const rightAddons = addons.filter((addon) => addon.align === addonAlignments.right);
    const hasLeftAddons = leftAddons && leftAddons.length > 0;
    const hasRightAddons = rightAddons && rightAddons.length > 0;
    const isLabelVisible = useMemo(() => (Boolean(label)), [
        label,
    ]);
    const placeholderVariationStyles = useMemo(() => (css `
        ${inputValueVerticalSpacingStyles[size]}
        align-self: flex-start;
      `), [
        size,
    ]);
    const baseInputContainerStyles = useInputContainerStyles({
        isDisabled: disabled,
        isFocused: isKeyboardFocused,
        isLabelVisible,
    });
    const inputContainerStyles = useMemo(() => (css `
        ${baseInputContainerStyles}
        flex-grow: 1;
      `), [
        baseInputContainerStyles,
    ]);
    const inputStyles = useMemo(() => (css `
        ${baseInputStyles}
        border: none !important; /* stylelint-disable-line declaration-no-important */
        box-shadow: none;
        position: absolute;
        ${isLabelVisible && placeholderVariationStyles}
        ${error && errorStyles}
        ${!hasLeftAddons && css `
          border-bottom-left-radius: ${theme.borderRadiusDefault};
          border-top-left-radius: ${theme.borderRadiusDefault};
        `}
        ${!hasRightAddons && css `
          border-bottom-right-radius: ${theme.borderRadiusDefault};
          border-top-right-radius: ${theme.borderRadiusDefault};
        `}
        ${error && !hasLeftAddons && css `
          border-left-color: ${theme.colors.negative800};
        `}
        ${error && !hasRightAddons && css `
          border-right-color: ${theme.colors.negative800};
        `}
      `), [
        error,
        hasLeftAddons,
        hasRightAddons,
        isLabelVisible,
        placeholderVariationStyles,
    ]);
    const focusInput = () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    const labelStyles = useLabelStyles({
        isValueVisible: (type === 'date'
            || Boolean(value)),
        size,
    });
    const basePlaceholderContentStyles = usePlaceholderContentStyles({
        isLabelVisible,
        isValueVisible: Boolean(value && !mask),
        size,
    });
    const placeholderContentStyles = useMemo(() => (css `
        ${basePlaceholderContentStyles}
        height: 100%;
        ${isLabelVisible && css `
          ${placeholderVariationStyles}
        `}
      `), [
        basePlaceholderContentStyles,
        isLabelVisible,
        placeholderVariationStyles,
    ]);
    const textInputContainerStyles = useErrorSpacingStyles({
        isErrorSpacingVisible: Boolean(hasErrorSpacing),
        size,
    });
    const textInputStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        ${styleUtils.transition([
        'border-color',
        'box-shadow',
    ])}
        background: ${theme.colors.gray100};
        border-radius: ${theme.borderRadiusDefault};
        border: ${styleUtils.rem(borderWidth)} solid ${theme.colors.gray400};
        display: flex;
        height: ${styleUtils.rem(inputHeights[size])};
        overflow: hidden;
        position: relative;
        width: 100%;

        &:focus-within {
          border: ${styleUtils.rem(borderWidth)} solid ${theme.colors.gray700};
        }

        ${error && touched && errorStyles}
        ${disabled && css `
          border-color: ${theme.colors.gray400};
        `}
        ${(borderType === borderTypes.none) && css `
          border-color: transparent;

          &:focus-within { /* stylelint-disable-line no-duplicate-selectors */
            border-color: transparent;
            box-shadow: none;
          }
        `}
        ${borderType === borderTypes.bottom && css `
          ${bottomBorderStyles}

          &:focus-within { /* stylelint-disable-line no-duplicate-selectors */
            ${bottomBorderStyles}
            box-shadow: none;
          }
        `}
        ${((borderType === borderTypes.bottom) && error && touched) && css `
          border-bottom-color: ${theme.colors.negative800};
        `}
        ${isKeyboardFocused && styleUtils.focusVisibleStyles}
      `), [
        borderType,
        disabled,
        error,
        isKeyboardFocused,
        size,
        touched,
    ]);
    return (_jsxs("div", Object.assign({ css: textInputContainerStyles }, { children: [_jsxs("div", Object.assign({ css: textInputStyles }, { children: [leftAddons.map((addon, index) => {
                        const key = `${name}Addon${index}`;
                        return (_jsx(TextInputAddon, Object.assign({}, addon, { disabled: disabled }), key));
                    }), _jsxs("div", Object.assign({ css: inputContainerStyles }, { children: [_jsx("input", Object.assign({}, rest, { "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, autoCapitalize: autoCapitalize, autoComplete: autoComplete, autoCorrect: autoCorrect, css: inputStyles, disabled: disabled, id: id || name, inputMode: inputMode, maxLength: maxLength, name: name, onBlur: onBlur, onChange: onChange, onFocus: onFocus, onKeyDown: onKeyDown, onKeyUp: onKeyUp, pattern: pattern, ref: inputRef, required: required, type: type, value: value })), label && (_jsxs("label", Object.assign({ css: labelStyles, htmlFor: name, onClick: focusInput }, { children: [_jsx("span", { children: label }), !value && hint && (_jsx("span", Object.assign({ css: helperTextStyles }, { children: hint }))), maxLength
                                        && isCounterVisible
                                        && value
                                        && (_jsxs("span", Object.assign({ css: counterStyles }, { children: [maxLength - value.length, "/", maxLength] })))] }))), placeholder && (_jsx("div", Object.assign({ css: placeholderContainerStyles }, { children: _jsx("div", Object.assign({ css: placeholderContentStyles, onClick: disabled ? undefined : focusInput }, { children: placeholder })) })))] })), rightAddons.map((addon, index) => {
                        const key = `${name}Addon${index}`;
                        return (_jsx(TextInputAddon, Object.assign({}, addon, { disabled: disabled }), key));
                    })] })), _jsx(FadeReveal, Object.assign({ in: !!error && !!touched, unmountOnExit: true }, { children: _jsx("div", Object.assign({ css: errorMessageStyles }, { children: _jsx("span", Object.assign({ role: "alert" }, { children: error })) })) }))] })));
};
const MemoizedTextInput = memo(TextInput);
export { 
// MC-1068 Temporary hack for Storybook.
MemoizedTextInput, TextInput, };
